import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../context/notificationContext";
import { useUserSettings } from "../../../context/userSettingsContext";
import useDataInitializer from "../../../hooks/useDataInitializer";
import { useI18n } from "../../../hooks/useTranslations";
import { useCostTypeCategoriesApi } from "../../../http/costTypeCategories/api";
import { UpsertCostTypeCategory } from "../../../http/costTypeCategories/types";
import ModalDialog from "../../ui/ModalDialog";
import CostTypeCategoryForm, { CostTypeCategoryFormBody } from "./CostTypeCategoryForm";

type EditCostTypeCategoryDialogProps = {
  costTypeCategoryId: string;
  onClose: () => void;
};

const EditCostTypeCategoryDialog: FC<EditCostTypeCategoryDialogProps> = ({
  costTypeCategoryId,
  onClose,
}: EditCostTypeCategoryDialogProps) => {
  const i18n = useI18n();
  const aborter = useRef(new AbortController());
  const userSettings = useUserSettings();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const costTypeCategoriesApi = useCostTypeCategoriesApi();

  const costTypeCategorySwr = costTypeCategoriesApi.getAdminCostTypeCategory(
    costTypeCategoryId,
    aborter.current.signal,
  );
  const [costTypeCategory, isInitialized] = useDataInitializer(costTypeCategorySwr.data, costTypeCategorySwr.error);

  useEffect(() => {
    costTypeCategoryId && costTypeCategorySwr.trigger();
  }, [costTypeCategoryId]);

  useEffect(() => {
    costTypeCategorySwr.error && dispatch(showErrorNotification(i18n, costTypeCategorySwr.error));
  }, [costTypeCategorySwr.error]);

  const onSubmit = async (formData: CostTypeCategoryFormBody): Promise<void> => {
    if (costTypeCategory?.id) {
      const body: UpsertCostTypeCategory = {
        name: formData.name,
        currentVersionNumber: costTypeCategory.versionNumber,
      };

      setIsSubmitting(true);
      try {
        await costTypeCategoriesApi.updateAdminCostTypeCategory(costTypeCategory.id, body);
        dispatch(showSuccessNotification(i18n.t));
        onClose();
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <ModalDialog onClose={onClose} title={i18n.t.costTypeCategories.edit}>
      <CostTypeCategoryForm
        onSubmit={onSubmit}
        isDisabled={!userSettings.permissions.costTypeCategories.update || isSubmitting || !costTypeCategory?.id}
        isLoading={!isInitialized}
        costTypeCategory={costTypeCategory}
      />
    </ModalDialog>
  );
};

export default EditCostTypeCategoryDialog;
